/**
 * soapNormalize — Defensa de visualización del SOAPE.
 *
 * Algunas notas quedaron persistidas con el JSON completo del modelo
 * (`{"soap":{…}}`, a veces envuelto en fences ```json) volcado dentro del campo
 * `subjective` (bug de parseo cuando la respuesta del modelo venía truncada).
 * Esto hacía que la sección Subjetivo mostrara JSON crudo.
 *
 * `normalizarSoap` detecta ese caso y devuelve los campos ya desanidados. El
 * backend ya no genera este dato; esto es para datos históricos.
 */

export interface SoapLike {
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  evolution?: string;
  education?: string;
}

function rescatarCampo(t: string, key: string): string {
  const m = t.match(new RegExp(`"${key}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"`, 'i'));
  if (!m) return '';
  try { return JSON.parse(`"${m[1]}"`) as string; } catch { return m[1]; }
}

export function normalizarSoap<T extends SoapLike>(soap: T | undefined | null): T | undefined {
  if (!soap) return soap ?? undefined;
  const subj = soap.subjective;
  if (typeof subj !== 'string') return soap;
  const pareceJson = /^\s*(```|\{)/.test(subj) && subj.includes('"soap"');
  if (!pareceJson) return soap;

  let t = subj.trim();
  const fence = t.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fence) t = fence[1].trim();

  const start = t.indexOf('{');
  const end = t.lastIndexOf('}');
  if (start >= 0 && end > start) {
    try {
      const obj = JSON.parse(t.slice(start, end + 1)) as { soap?: SoapLike };
      if (obj?.soap) return { ...soap, ...obj.soap };
    } catch { /* rescate por regex abajo */ }
  }
  // JSON truncado: rescatar strings bien terminadas.
  return {
    ...soap,
    subjective: rescatarCampo(t, 'subjective'),
    objective: rescatarCampo(t, 'objective'),
    assessment: rescatarCampo(t, 'assessment'),
    plan: rescatarCampo(t, 'plan'),
  };
}
